import {
  faBoltLightning,
  faBook,
  faBuilding,
  faCalendar,
  faCalendarWeek,
  faChalkboardTeacher,
  faCircleDollar,
  faClipboard,
  faClock,
  faComments,
  faDumbbell,
  faFileCertificate,
  faFileCircleQuestion,
  faFolder,
  faGraduationCap,
  faHandHoldingHeart,
  faHandshake,
  faHeadSideBrain,
  faLaptop,
  faLightbulb,
  faMagnifyingGlass,
  faMessagesQuestion,
  faPencil,
  faSearch,
  faStar,
  faUserGroup,
  faUsers,
  faUsersBetweenLines,
} from '@fortawesome/pro-solid-svg-icons'
import { DateTime } from 'luxon'

import {
  CAW_APPLICATION_OFFERING_SLUG,
  CAW_COLLEGE_LIST_OFFERING_SLUG,
  CAW_COLLEGE_LIST_WORKSHOP,
  CAW_ESSAY_OFFERING_SLUG,
  CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
  CAW_FINANCIAL_AID_DECODED_WORKSHOP,
  CAW_GETTING_UNSTUCK_ESSAY_OFFERING_SLUG,
  CAW_GETTING_UNSTUCK_ESSAY_WORKSHOP,
  CAW_WORKSHOP_APPLICATION,
  CAW_WORKSHOP_ESSAY,
} from '@/queries/constants'
import { getArticleUrl } from '@/client/services/url/faq'

import type { IconBulletItem } from '@/components/UI/IconBullets'

import type {
  ProgramBenefit,
  ProgramDetails,
  ProgramEmailData,
  ProgramFactoid,
  ProgramIteration,
  ProgramMarketingConfig,
  ProgramOfferingTemplate,
} from '../programs/types'
import composeRichText from '../richText/composeRichText'
import { getPublicUrl } from '../url'
import {
  COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
  COLLEGE_PREP_WORLD_SLUG,
} from '../worlds/constants'

export const CAW_TUTOR_LANDING_PAGE_REF = 'CAW-LANDING-PAGE'
export const CAW_LEARNER_LANDING_PAGE_REF = 'CAW-LEARNER-LANDING-PAGE'

export const CAW_TUTOR_REFERRAL_CODES = [
  'NPSS-CAW-TUTOR',
  'FELLOWSHIP-CAW-TUTOR',
  'USC-CAW-TUTOR',
  'BERKELEY-CAW-TUTOR',
  'COALITION-CAW-TUTOR',
  'COALITION-CBO-CAW-TUTOR',
  'COALITION-STUDENT-CAW-TUTOR',
  'COALITION-CSB-TUTOR',
  'COALITION-MEMBER-CAW-TUTOR',
  'COALITION-COUNSELOR-CAW-TUTOR',
  CAW_TUTOR_LANDING_PAGE_REF,
  'LI-CAW-TUTOR',
  'ASPIRE-CAW-TUTOR',
  'HANDSHAKE-CAW-TUTOR',
  'CAW-TUTOR-REFERRAL',
  'APO-CAW-TUTOR',
  'FWS-CAW-TUTOR',
]

export const CAW_LEARNER_REFERRAL_CODES = [
  CAW_LEARNER_LANDING_PAGE_REF,
  'COALITION-CAW-LEARNER',
  'COALITION-MEMBER-CAW-LEARNER',
]

export type MarketingCopy = {
  theEssayDescription: string
  theApplicationDescription: string
  findingYourFitDescription: string
  financialAidDecodedDescription: string
  signUpCardHeading: string
  signUpCardText: string
}

export const CAW_MARKETING_COPY_LEARNER: MarketingCopy = {
  theEssayDescription: 'Learn how to craft college essays that captivate admissions officers',
  theApplicationDescription: 'Get guidance on completing your college applications',
  findingYourFitDescription:
    'Research colleges strategically and build a balanced list matched to your goals and interests',
  financialAidDecodedDescription:
    'Level up your understanding of college costs and gain a clearer picture of the financial aid process',
  signUpCardHeading: 'Sign Up',
  signUpCardText:
    'Join one of our programs and receive personalized help through every step of the admissions process.',
}

export const CAW_MARKETING_COPY_TUTOR: MarketingCopy = {
  theEssayDescription: 'Help students craft compelling essays that stand out',
  theApplicationDescription: 'Assist students in completing their college applications',
  findingYourFitDescription:
    'Guide students in researching colleges and creating strategic application lists',
  financialAidDecodedDescription:
    'Explain college costs and how to navigate the financial aid process',
  signUpCardHeading: 'Host a College Admissions Workshop',
  signUpCardText:
    'Make a meaningful impact by supporting students through college admissions milestones.',
}

export const CAW_ITERATION_SLUGS = [
  'september-2024',
  'october-2024',
  'november-2024',
  'february-2025',
  'march-2025',
  'april-2025',
  'may-2025',
  'june-2025',
  'july-2025',
  'august-2025',
  'september-2025',
  'october-2025',
  'november-2025',
  'march-2026',
  'april-2026',
  'may-2026',
  'june-2026',
  'july-2026',
  'august-2026',
  'september-2026',
  'october-2026',
  'november-2026',
] as const
export type CAWIterationSlug = (typeof CAW_ITERATION_SLUGS)[number]

const CAW_WHAT_YOULL_DO_BULLETS_TUTOR: IconBulletItem[] = [
  {
    key: 'guide-students',
    icon: faBook,
    body: 'Guide students in exploring colleges and building strategic school lists',
  },
  {
    key: 'craft-essays',
    icon: faPencil,
    body: 'Support learners through brainstorming, writing, and revising essays',
  },
  {
    key: 'master-process',
    icon: faGraduationCap,
    body: 'Explain college affordability and how to navigate the financial aid process',
  },
  {
    key: 'personalized-assistance',
    icon: faComments,
    body: 'Share encouragement and your experiences with your learners',
  },
]

const WHAT_YOULL_DO_ICONS_COLOR = 'blue500'

const THE_ESSAY_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: CAW_ESSAY_OFFERING_SLUG,
  title: CAW_WORKSHOP_ESSAY,
  numWeeks: 4,
  sessionFrequency: { times: 2, per: 'week' },
  sessionLength: 75,
  imageUrl: getPublicUrl('programs/offerings/i-caw-theessay-2.png'),
  learnerDescription: CAW_MARKETING_COPY_LEARNER.theEssayDescription,
  learnerDescriptionLong:
    'Craft compelling personal statements and supplemental essays through guided practice, collaborative learning, and expert strategies designed to help you stand out in your college applications.',
  wizardIcon: faPencil,
  whatYoullDo: {
    bulletsLearner: [
      {
        key: 'purpose',
        icon: faBook,
        body: 'Learn the purpose and the must-dos of the personal statement',
      },
      {
        key: 'strategies',
        icon: faPencil,
        body: 'Learn and practice strategies for picking a prompt, brainstorming, drafting, and revising your writing',
      },
      {
        key: 'supplemental',
        icon: faClipboard,
        body: 'Understand the role of supplemental questions and strategies for organizing your questions and responses',
      },
      {
        key: 'collaborate',
        icon: faUsers,
        body: 'Work alongside other students who are also preparing to apply to college',
      },
    ],
    bulletsTutor: CAW_WHAT_YOULL_DO_BULLETS_TUTOR,
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
  },
}

export const THE_APPLICATION_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: CAW_APPLICATION_OFFERING_SLUG,
  title: CAW_WORKSHOP_APPLICATION,
  sessionFrequency: { times: 2, per: 'week' },
  sessionLength: 60,
  numWeeks: 2,
  imageUrl: getPublicUrl('programs/offerings/i-caw-application-2.png'),
  learnerDescription: CAW_MARKETING_COPY_LEARNER.theApplicationDescription,
  learnerDescriptionLong:
    'Navigate the college application process with confidence as you better understand the application timeline, draft key components of your application, and collaborate with peers to create a standout application.',
  wizardIcon: faFolder,
  whatYoullDo: {
    bulletsLearner: [
      {
        key: 'purpose',
        icon: faBook,
        body: 'Learn the purpose of each part of the application',
      },
      {
        key: 'strategy',
        icon: faCalendar,
        body: 'Understand how and when to apply and strategies to consider when making your plan',
      },
      {
        key: 'drafts',
        icon: faClipboard,
        body: 'Complete drafts of frequently required parts of the application, such as lists of activities & experiences, and honors & awards',
      },
      {
        key: 'collaborate',
        icon: faUsers,
        body: 'Work alongside other students who are also preparing to apply to college',
      },
    ],
    bulletsTutor: CAW_WHAT_YOULL_DO_BULLETS_TUTOR,
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
  },
}

export const COLLEGE_LIST_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: CAW_COLLEGE_LIST_OFFERING_SLUG,
  title: CAW_COLLEGE_LIST_WORKSHOP,
  sessionFrequency: { times: 2, per: 'week' },
  sessionLength: 75,
  numWeeks: 4,
  imageUrl: getPublicUrl('programs/offerings/i-caw-findingyourfit.png'),
  learnerDescription: CAW_MARKETING_COPY_LEARNER.findingYourFitDescription,
  learnerDescriptionLong:
    'Research colleges and build a balanced list using BigFuture. Learn how to evaluate social, academic, and financial fit to create a thoughtful mix of reach, target, and safety schools.',
  wizardIcon: faMagnifyingGlass,
  whatYoullDo: {
    bulletsLearner: [
      {
        key: 'evaluate',
        icon: faLightbulb,
        body: 'Gain a clearer picture of the financial aid process',
      },
      {
        key: 'research',
        icon: faFileCircleQuestion,
        body: 'Learn about what information is required to complete key forms and how to collect it',
      },
      {
        key: 'list',
        icon: faClipboard,
        body: "Understand the forms you'll be asked to submit",
      },
      {
        key: 'explore',
        icon: faUsersBetweenLines,
        body: 'Work alongside other learners who are also applying to college',
      },
    ],
    bulletsTutor: CAW_WHAT_YOULL_DO_BULLETS_TUTOR,
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
  },
}

export const FINANCIAL_AID_DECODED_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
  title: CAW_FINANCIAL_AID_DECODED_WORKSHOP,
  sessionFrequency: { times: 2, per: 'week' },
  sessionLength: 60,
  numWeeks: 2,
  imageUrl: getPublicUrl('programs/offerings/i-caw-financial-aid-decoded-2.png'),
  learnerDescription: CAW_MARKETING_COPY_LEARNER.financialAidDecodedDescription,
  learnerDescriptionLong:
    "Gain a clearer picture of the financial aid process, which can help make college affordable. Learn about the forms you'll submit and how to make affordable choices throughout your college journey.",
  wizardIcon: faCircleDollar,
  whatYoullDo: {
    bulletsLearner: [
      {
        key: 'evaluate',
        icon: faSearch,
        body: 'Learn how to evaluate colleges across key factors like academics, campus life, location, and cost to find your best matches',
      },
      {
        key: 'research',
        icon: faLaptop,
        body: 'Use online tools and resources to research schools thoroughly, looking beyond rankings to discover what makes each unique',
      },
      {
        key: 'list',
        icon: faClipboard,
        body: 'Create a balanced list of reach, target, and safety schools that align with your academic profile and preferences',
      },
      {
        key: 'explore',
        icon: faBuilding,
        body: 'Develop strategies for exploring colleges through virtual tours, campus visits, and conversations with students and staff',
      },
    ],
    bulletsTutor: CAW_WHAT_YOULL_DO_BULLETS_TUTOR,
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
  },
}

export const GETTING_UNSTUCK_ESSAY_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: CAW_GETTING_UNSTUCK_ESSAY_OFFERING_SLUG,
  title: CAW_GETTING_UNSTUCK_ESSAY_WORKSHOP,
  sessionLength: 75,
  imageUrl: getPublicUrl('programs/offerings/i-caw-theessay-onetime.svg'),
  learnerDescription:
    'A one-time workshop to brainstorm college essay topics and choose what to write about',
  learnerDescriptionLong:
    'Struggling with your college essay? Join our "Getting Started on Your College Essay" workshop—a 75-minute session with interactive exercises to help you brainstorm topics, build in reflection, and create a plan to move forward with your essay.',
  wizardIcon: faPencil,
  whatYoullDo: {
    bulletsLearner: [
      {
        key: 'brainstorm',
        icon: faLightbulb,
        body: 'Brainstorm a list of potential topics to write about for your college essay',
      },
      {
        key: 'strategies',
        icon: faPencil,
        body: 'Learn strategies for how to build in reflection for your essay and choose a good topic',
      },
      {
        key: 'next-steps',
        icon: faClipboard,
        body: 'Have a list of next steps that you can use to continue writing your essay',
      },
      {
        key: 'collaborate',
        icon: faUsers,
        body: 'Work alongside other students who are also preparing to apply to college',
      },
    ],
    bulletsTutor: CAW_WHAT_YOULL_DO_BULLETS_TUTOR,
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
  },
}

export const CAW_ITERATIONS: Record<CAWIterationSlug, ProgramIteration> = {
  'september-2024': {
    iterationSlug: 'september-2024',
    title: 'September',
    learnerRegistrationOpens: DateTime.local(2024, 8, 26, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    learnerRegistrationDeadline: DateTime.local(2024, 9, 5, 23, 59, {
      zone: 'America/New_York',
    }),
    tutorRegistrationOpens: DateTime.local(2024, 8, 12, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    tutorRegistrationDeadline: DateTime.local(2024, 9, 1, 23, 59, {
      zone: 'America/New_York',
    }),
    startDate: DateTime.local(2024, 9, 6, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2024, 10, 3, 23, 59, {
      zone: 'America/New_York',
    }),
    offerings: [
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2024, 9, 6, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2024, 10, 3, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2024-09',
        learnerPostSessionSurveyUrl: 'https://form.typeform.com/to/yRLowCNN',
      },
      {
        offeringTemplate: THE_APPLICATION_OFFERING_TEMPLATE,
        startDate: DateTime.local(2024, 9, 6, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2024, 9, 19, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-application-2024-09',
        learnerPostSessionSurveyUrl: 'https://form.typeform.com/to/bEvqENA8',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.66,
      },
      {
        offeringSlug: CAW_APPLICATION_OFFERING_SLUG,
        ratio: 0.34,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/rexPBWiO',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/NyvvjqaY',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/tMx6vvJw',
  },
  'october-2024': {
    iterationSlug: 'october-2024',
    title: 'October',
    learnerRegistrationOpens: DateTime.local(2024, 9, 19, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    learnerRegistrationDeadline: DateTime.local(2024, 10, 3, 23, 59, {
      zone: 'America/New_York',
    }),
    tutorRegistrationOpens: DateTime.local(2024, 9, 5, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    tutorRegistrationDeadline: DateTime.local(2024, 9, 30, 23, 59, {
      zone: 'America/New_York',
    }),
    startDate: DateTime.local(2024, 10, 4, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2024, 10, 31, 23, 59, {
      zone: 'America/New_York',
    }),
    offerings: [
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2024, 10, 4, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2024, 10, 31, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2024-10',
      },
      {
        offeringTemplate: THE_APPLICATION_OFFERING_TEMPLATE,
        startDate: DateTime.local(2024, 10, 4, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2024, 10, 17, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-application-2024-10',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.66,
      },
      {
        offeringSlug: CAW_APPLICATION_OFFERING_SLUG,
        ratio: 0.34,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/vOY5DI5M',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/qMEDofcY',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/wcGYLiZH',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/p0S263fv',
  },
  'november-2024': {
    iterationSlug: 'november-2024',
    title: 'November',
    learnerRegistrationOpens: DateTime.local(2024, 10, 28, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    learnerRegistrationDeadline: DateTime.local(2024, 11, 7, 23, 59, {
      zone: 'America/New_York',
    }),
    tutorRegistrationOpens: DateTime.local(2024, 10, 14, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    tutorRegistrationDeadline: DateTime.local(2024, 11, 4, 23, 59, {
      zone: 'America/New_York',
    }),
    startDate: DateTime.local(2024, 11, 8, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2024, 12, 5, 23, 59, {
      zone: 'America/New_York',
    }),
    // TODO: when configuring the offerings templateIds, make sure to configure the recommended hosting ratios too!
    offerings: [
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2024, 11, 8, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2024, 12, 5, 23, 59, { zone: 'America/New_York' }),
        // TODO: either create this template, or allow creating this section without a linked template
        seriesTemplateSlug: 'caw-essay-2024-11',
      },
      {
        offeringTemplate: THE_APPLICATION_OFFERING_TEMPLATE,
        startDate: DateTime.local(2024, 11, 8, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2024, 12, 5, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-application-2024-11',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.66,
      },
      {
        offeringSlug: CAW_APPLICATION_OFFERING_SLUG,
        ratio: 0.34,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/U4hfx7Tv',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/iA4hLimv',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/mMGSXeO6',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/xYZf3Hlk',
  },
  'february-2025': {
    iterationSlug: 'february-2025',
    title: 'February',
    learnerRegistrationOpens: DateTime.local(2025, 1, 23, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    learnerRegistrationDeadline: DateTime.local(2025, 2, 6, 23, 59, {
      zone: 'America/New_York',
    }),
    tutorRegistrationOpens: DateTime.local(2025, 1, 13, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    tutorRegistrationDeadline: DateTime.local(2025, 2, 3, 23, 59, {
      zone: 'America/New_York',
    }),
    startDate: DateTime.local(2025, 2, 7, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 3, 6, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: COLLEGE_LIST_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 2, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 3, 6, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-finding-your-fit-2025-02',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_COLLEGE_LIST_OFFERING_SLUG,
        ratio: 1,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/Jf4TEz4W',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/JnISI5FE',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/cU2GF32U',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/SeJHYEVj',
  },
  'march-2025': {
    iterationSlug: 'march-2025',
    title: 'March',
    learnerRegistrationOpens: DateTime.local(2025, 2, 20, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 3, 6, 23, 59, { zone: 'America/New_York' }),
    tutorRegistrationOpens: DateTime.local(2025, 2, 10, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 2, 24, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 3, 7, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 4, 3, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: COLLEGE_LIST_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 3, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 3, 20, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-finding-your-fit-2025-03',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_COLLEGE_LIST_OFFERING_SLUG,
        ratio: 1,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/KpELZajc',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/ovikJbnM',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/ipmaieks',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/yUzqHeT3',
  },
  'april-2025': {
    iterationSlug: 'april-2025',
    title: 'April',
    learnerRegistrationOpens: DateTime.local(2025, 3, 19, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 4, 3, 23, 59, { zone: 'America/New_York' }),
    tutorRegistrationOpens: DateTime.local(2025, 3, 10, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 3, 24, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 4, 4, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 5, 1, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 4, 4, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 4, 17, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2025-04',
      },
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 4, 4, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 5, 1, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2025-04',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 0.5,
      },
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.5,
      },
    ],
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/UEL7dciq',
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/o0RnSy9L',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/g2SAjMF3',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/uA1ykNPW',
  },
  'may-2025': {
    iterationSlug: 'may-2025',
    title: 'May',
    learnerRegistrationOpens: DateTime.local(2025, 4, 18, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 5, 8, 23, 59, { zone: 'America/New_York' }),
    tutorRegistrationOpens: DateTime.local(2025, 4, 14, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 4, 28, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 5, 9, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 6, 5, 22, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 5, 9, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 5, 22, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2025-05',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 1,
      },
    ],
  },
  'june-2025': {
    iterationSlug: 'june-2025',
    title: 'June',
    learnerRegistrationOpens: DateTime.local(2025, 5, 23, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 6, 8, 23, 59, { zone: 'America/New_York' }),
    tutorRegistrationOpens: DateTime.local(2025, 5, 9, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 5, 30, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 6, 6, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 7, 3, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: COLLEGE_LIST_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 6, 6, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 7, 3, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-finding-your-fit-2025-06',
      },
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 6, 6, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 7, 3, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2025-06',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_COLLEGE_LIST_OFFERING_SLUG,
        ratio: 0.5,
      },
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.5,
      },
    ],
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/jVJ3FRZ5',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/O9ZNtI47',
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/jSQ4oX4h',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/BniBJsXr',
  },
  'july-2025': {
    iterationSlug: 'july-2025',
    title: 'July',
    learnerRegistrationOpens: DateTime.local(2025, 6, 27, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 7, 10, 23, 59, { zone: 'America/New_York' }),
    tutorRegistrationOpens: DateTime.local(2025, 6, 13, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 7, 4, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 7, 11, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 8, 7, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: COLLEGE_LIST_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 7, 11, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 8, 7, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-college-list-2025-07',
      },
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 7, 11, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 8, 7, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2025-07',
      },
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 7, 11, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 7, 24, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2025-07',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 0.25,
      },
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.5,
      },
      {
        offeringSlug: CAW_COLLEGE_LIST_OFFERING_SLUG,
        ratio: 0.25,
      },
    ],
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/l5tti7PL',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/fsjqu4hP',
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/GFCElXua',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/FM19Dg4K',
  },
  'august-2025': {
    iterationSlug: 'august-2025',
    title: 'August',
    learnerRegistrationOpens: DateTime.local(2025, 7, 25, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 8, 7, 23, 59, { zone: 'America/New_York' }),
    tutorRegistrationOpens: DateTime.local(2025, 7, 11, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 7, 28, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 8, 8, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 9, 4, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 8, 8, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 9, 4, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2025-08',
      },
      {
        offeringTemplate: THE_APPLICATION_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 8, 8, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 8, 21, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-application-2025-08',
      },
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 8, 8, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 8, 21, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2025-08',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 0.2,
      },
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.4,
      },
      {
        offeringSlug: CAW_APPLICATION_OFFERING_SLUG,
        ratio: 0.4,
      },
    ],
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/pKg38LKB',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/P8V6DKzH',
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/hCq3yNvz',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/pvAVzPhv',
  },
  'september-2025': {
    iterationSlug: 'september-2025',
    title: 'September',
    tutorRegistrationOpens: DateTime.local(2025, 8, 11, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 8, 25, 23, 59, { zone: 'America/New_York' }),
    learnerRegistrationOpens: DateTime.local(2025, 8, 21, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 9, 4, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 9, 5, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 10, 2, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 9, 5, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 10, 2, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2025-09',
      },
      {
        offeringTemplate: THE_APPLICATION_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 9, 5, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 9, 18, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-application-2025-09',
      },
      {
        offeringTemplate: COLLEGE_LIST_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 9, 5, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 10, 2, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-college-list-2025-09',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_COLLEGE_LIST_OFFERING_SLUG,
        ratio: 0.2,
      },
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.4,
      },
      {
        offeringSlug: CAW_APPLICATION_OFFERING_SLUG,
        ratio: 0.4,
      },
    ],
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/hzbNnMoo',
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/pTawZZER',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/qBhZG3l7',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/qm72to2b',
  },
  'october-2025': {
    iterationSlug: 'october-2025',
    title: 'October',
    tutorRegistrationOpens: DateTime.local(2025, 9, 15, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 9, 29, 23, 59, { zone: 'America/New_York' }),
    learnerRegistrationOpens: DateTime.local(2025, 9, 25, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 10, 9, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 10, 10, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 11, 6, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 10, 10, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 11, 6, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2025-10',
      },
      {
        offeringTemplate: THE_APPLICATION_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 10, 10, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 10, 23, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-application-2025-10',
      },
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 10, 10, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 10, 23, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2025-10',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 0.2,
      },
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.4,
      },
      {
        offeringSlug: CAW_APPLICATION_OFFERING_SLUG,
        ratio: 0.4,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/ZrdHzjzW',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/trM30iVZ',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/ETTFEjPt',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/AYYD7I9x',
  },
  'november-2025': {
    iterationSlug: 'november-2025',
    title: 'November',
    tutorRegistrationOpens: DateTime.local(2025, 10, 13, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 10, 27, 23, 59, { zone: 'America/New_York' }),
    learnerRegistrationOpens: DateTime.local(2025, 10, 23, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    learnerRegistrationDeadline: DateTime.local(2025, 11, 6, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 11, 7, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 11, 20, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: GETTING_UNSTUCK_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 11, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 11, 20, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-one-time-2025-11',
      },
      {
        offeringTemplate: THE_APPLICATION_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 11, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 11, 20, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-application-2025-11',
      },
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 11, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 11, 20, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2025-11',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_APPLICATION_OFFERING_SLUG,
        ratio: 0.33,
      },
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 0.33,
      },
      {
        offeringSlug: CAW_GETTING_UNSTUCK_ESSAY_OFFERING_SLUG,
        ratio: 0.34,
      },
    ],
  },
  'march-2026': {
    iterationSlug: 'march-2026',
    title: 'March',
    tutorRegistrationOpens: DateTime.local(2026, 2, 3, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2026, 2, 17, 23, 59, { zone: 'America/New_York' }),
    learnerRegistrationOpens: DateTime.local(2026, 2, 13, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2026, 3, 1, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2026, 3, 2, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2026, 3, 30, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: COLLEGE_LIST_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 3, 2, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 3, 30, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-college-list-2026-03',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_COLLEGE_LIST_OFFERING_SLUG,
        ratio: 1,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/iCAZbIH4',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/z5xTLA2X',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/b0650P0U',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/U7ZIri4C',
  },
  'april-2026': {
    iterationSlug: 'april-2026',
    title: 'April',
    tutorRegistrationOpens: DateTime.local(2026, 3, 6, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2026, 3, 27, 23, 59, { zone: 'America/New_York' }),
    learnerRegistrationOpens: DateTime.local(2026, 3, 17, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2026, 3, 31, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2026, 4, 1, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2026, 4, 29, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 4, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 4, 29, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2026-03',
      },
      {
        offeringTemplate: COLLEGE_LIST_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 4, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 4, 29, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-college-list-2026-04',
      },
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 4, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 4, 14, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2026-03',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.5,
      },
      {
        offeringSlug: CAW_COLLEGE_LIST_OFFERING_SLUG,
        ratio: 0.25,
      },
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 0.25,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/mofgV5Ni',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/iBcVgY57',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/riOJmqdN',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/YOb3LKnK',
  },
  'may-2026': {
    iterationSlug: 'may-2026',
    title: 'May',
    tutorRegistrationOpens: DateTime.local(2026, 4, 6, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2026, 4, 20, 23, 59, { zone: 'America/New_York' }),
    learnerRegistrationOpens: DateTime.local(2026, 4, 16, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2026, 4, 30, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2026, 5, 1, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2026, 5, 29, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: COLLEGE_LIST_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 5, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 5, 29, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-college-list-2026-05',
      },
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 5, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 5, 14, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2026-05',
      },
      {
        offeringTemplate: GETTING_UNSTUCK_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 5, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 5, 29, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-one-time-2026-05',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_COLLEGE_LIST_OFFERING_SLUG,
        ratio: 0.33,
      },
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 0.33,
      },
      {
        offeringSlug: CAW_GETTING_UNSTUCK_ESSAY_OFFERING_SLUG,
        ratio: 0.34,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/Y3DB8BaP',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/ms2P9xlD',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/exfuUEj7',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/mBoOdNiH',
  },
  'june-2026': {
    iterationSlug: 'june-2026',
    title: 'June',
    tutorRegistrationOpens: DateTime.local(2026, 5, 5, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2026, 5, 19, 23, 59, { zone: 'America/New_York' }),
    learnerRegistrationOpens: DateTime.local(2026, 5, 15, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2026, 5, 31, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2026, 6, 1, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2026, 6, 29, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 6, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 6, 29, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2026-06',
      },
      {
        offeringTemplate: COLLEGE_LIST_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 6, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 6, 29, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-college-list-2026-06',
      },
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 6, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 6, 14, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2026-06',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.5,
      },
      {
        offeringSlug: CAW_COLLEGE_LIST_OFFERING_SLUG,
        ratio: 0.25,
      },
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 0.25,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/c9oC2JER',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/onYv6E4t',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/a7B6ndCl',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/eYexfkBE',
  },
  'july-2026': {
    iterationSlug: 'july-2026',
    title: 'July',
    tutorRegistrationOpens: DateTime.local(2026, 6, 5, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2026, 6, 19, 23, 59, { zone: 'America/New_York' }),
    learnerRegistrationOpens: DateTime.local(2026, 6, 16, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2026, 6, 30, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2026, 7, 1, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2026, 7, 29, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 7, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 7, 29, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2026-07',
      },
      {
        offeringTemplate: COLLEGE_LIST_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 7, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 7, 29, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-college-list-2026-07',
      },
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 7, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 7, 14, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2026-07',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.5,
      },
      {
        offeringSlug: CAW_COLLEGE_LIST_OFFERING_SLUG,
        ratio: 0.25,
      },
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 0.25,
      },
    ],
  },
  'august-2026': {
    iterationSlug: 'august-2026',
    title: 'August',
    tutorRegistrationOpens: DateTime.local(2026, 7, 7, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2026, 7, 21, 23, 59, { zone: 'America/New_York' }),
    learnerRegistrationOpens: DateTime.local(2026, 7, 17, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2026, 8, 2, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2026, 8, 3, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2026, 8, 31, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 8, 3, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 8, 31, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2026-08',
      },
      {
        offeringTemplate: THE_APPLICATION_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 8, 3, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 8, 16, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-application-2026-08',
      },
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 8, 3, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 8, 16, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2026-08',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.4,
      },
      {
        offeringSlug: CAW_APPLICATION_OFFERING_SLUG,
        ratio: 0.4,
      },
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 0.2,
      },
    ],
  },
  'september-2026': {
    iterationSlug: 'september-2026',
    title: 'September',
    tutorRegistrationOpens: DateTime.local(2026, 8, 7, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2026, 8, 21, 23, 59, { zone: 'America/New_York' }),
    learnerRegistrationOpens: DateTime.local(2026, 8, 17, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2026, 8, 31, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2026, 9, 1, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2026, 9, 29, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 9, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 9, 29, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2026-09',
      },
      {
        offeringTemplate: THE_APPLICATION_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 9, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 9, 14, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-application-2026-09',
      },
      {
        offeringTemplate: COLLEGE_LIST_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 9, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 9, 29, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-college-list-2026-09',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.4,
      },
      {
        offeringSlug: CAW_APPLICATION_OFFERING_SLUG,
        ratio: 0.4,
      },
      {
        offeringSlug: CAW_COLLEGE_LIST_OFFERING_SLUG,
        ratio: 0.2,
      },
    ],
  },
  'october-2026': {
    iterationSlug: 'october-2026',
    title: 'October',
    tutorRegistrationOpens: DateTime.local(2026, 9, 7, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2026, 9, 21, 23, 59, { zone: 'America/New_York' }),
    learnerRegistrationOpens: DateTime.local(2026, 9, 16, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2026, 9, 30, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2026, 10, 1, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2026, 10, 29, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: THE_ESSAY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 10, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 10, 29, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-essay-2026-10',
      },
      {
        offeringTemplate: THE_APPLICATION_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 10, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 10, 14, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-application-2026-10',
      },
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 10, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 10, 14, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2026-10',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_ESSAY_OFFERING_SLUG,
        ratio: 0.4,
      },
      {
        offeringSlug: CAW_APPLICATION_OFFERING_SLUG,
        ratio: 0.4,
      },
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 0.2,
      },
    ],
  },
  'november-2026': {
    iterationSlug: 'november-2026',
    title: 'November',
    tutorRegistrationOpens: DateTime.local(2026, 10, 6, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2026, 10, 20, 23, 59, { zone: 'America/New_York' }),
    learnerRegistrationOpens: DateTime.local(2026, 10, 16, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    learnerRegistrationDeadline: DateTime.local(2026, 11, 1, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2026, 11, 2, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2026, 11, 30, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: THE_APPLICATION_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 11, 2, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 11, 15, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-application-2026-11',
      },
      {
        offeringTemplate: FINANCIAL_AID_DECODED_OFFERING_TEMPLATE,
        startDate: DateTime.local(2026, 11, 2, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 11, 15, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'caw-financial-aid-2026-11',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: CAW_APPLICATION_OFFERING_SLUG,
        ratio: 0.5,
      },
      {
        offeringSlug: CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG,
        ratio: 0.5,
      },
    ],
  },
}

export const CAW_PROGRAM_TUTOR_BENEFITS: ProgramBenefit[] = [
  {
    id: 'give-back',
    text: 'Give back to other students using your experience with the college application process',
    faIcon: faHandHoldingHeart,
    iconProps: { color: 'green500' },
  },
  {
    id: 'tutor-community',
    text: 'Join a global community of more than 11,000 tutors',
    faIcon: faUserGroup,
    iconProps: { color: 'blue500' },
  },
  {
    id: 'expert-curriculum',
    text: 'Receive guidance from seasoned college admissions experts',
    faIcon: faHandshake,
    iconProps: { color: 'yellow700' },
  },
  {
    id: 'volunteer-hours',
    text: 'Earn certified volunteer hours to add to your resume.',
    faIcon: faFileCertificate,
    iconProps: { color: 'purple500' },
  },
]

export const CAW_PROGRAM_LEARNER_BENEFITS: ProgramBenefit[] = [
  {
    id: 'college-admissions-process',
    text: composeRichText(ctx =>
      ctx.paragraph(
        ctx.text('Join workshops tailored to different aspects of the '),
        ctx.text('college admissions process', { bold: true }),
        ctx.text('.')
      )
    ),
    faIcon: faBook,
    iconProps: { color: 'blue50', backgroundColor: 'blue600' },
  },
  {
    id: 'score-increase',
    text: composeRichText(ctx =>
      ctx.paragraph(
        ctx.text('Create '),
        ctx.text('accountability with structured time', { bold: true }),
        ctx.text(' to make progress on the application.')
      )
    ),
    faIcon: faHeadSideBrain,
    iconProps: { color: 'blue50', backgroundColor: 'blue600' },
  },
  {
    id: 'personalized-peer-tutor',
    text: composeRichText(ctx =>
      ctx.paragraph(
        ctx.text('Receive personalized support from a '),
        ctx.text('high-performing peer tutor', { bold: true }),
        ctx.text('.')
      )
    ),
    faIcon: faUserGroup,
    iconProps: { color: 'blue50', backgroundColor: 'blue600' },
  },
  {
    id: 'speedy',
    text: composeRichText(ctx =>
      ctx.paragraph(
        ctx.text('Get started in just a few minutes. Our workshops are '),
        ctx.text('100% free and non-profit', { bold: true }),
        ctx.text('.')
      )
    ),
    faIcon: faBoltLightning,
    iconProps: { color: 'blue50', backgroundColor: 'blue600' },
  },
]

export const CAW_PROGRAM_FACTOIDS: ProgramFactoid[] = [
  {
    key: 'program-duration',
    text: '2-4 weeks',
    subtext: '2 sessions/week',
    iconProps: { size: 'md', color: 'blue600', faIcon: faCalendarWeek },
  },
  {
    key: 'session-duration',
    text: '60-75 minutes',
    subtext: 'Per session',
    iconProps: { size: 'md', color: 'blue600', faIcon: faClock },
  },
  {
    key: 'max-session-capacity',
    text: '15 learners',
    subtext: 'Max. class size',
    iconProps: { size: 'md', color: 'blue600', faIcon: faUsers },
  },
]

export const CAW_MARKETING_CONFIG: ProgramMarketingConfig = {
  header: {
    learnerText: 'A program made for future college students',
    learnerSubtext:
      'Unlock your college potential with support through every step of the college admissions process from experienced tutors.',
    tutorText: 'A program made for future college students',
    tutorSubtext:
      'Join our mission to empower high school students to navigate college admissions with confidence.',
    backgroundImageUrl: 'college-admissions-workshops/CAW-tutor-marketing-background-v2.png',
    backgroundColor: 'teal.700',
    factoids: CAW_PROGRAM_FACTOIDS,
    partnerships: ['Coalition', 'BigFuture'],
  },
  tutorSignUpCard: CAW_MARKETING_COPY_TUTOR,
  learnerSignUpCard: CAW_MARKETING_COPY_LEARNER,
  tutorBenefits: CAW_PROGRAM_TUTOR_BENEFITS,
  learnerBenefits: CAW_PROGRAM_LEARNER_BENEFITS,
  programFactoids: CAW_PROGRAM_FACTOIDS,
  whatYoullDo: {
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
    bulletsLearner: [
      {
        key: 'research-colleges',
        icon: faBook,
        body: 'Research colleges and develop a balanced list that matches your goals',
      },
      {
        key: 'craft-essays',
        icon: faPencil,
        body: 'Craft and polish authentic essays that showcase your unique story',
      },
      {
        key: 'master-process',
        icon: faMessagesQuestion,
        body: 'Master the application process with structured guidance and feedback',
      },
      {
        key: 'stay-motivated',
        icon: faDumbbell,
        body: 'Level up your understanding of college affordability and financial aid',
      },
    ],
    bulletsTutor: CAW_WHAT_YOULL_DO_BULLETS_TUTOR,
  },
}

export const CAW_LEARNER_WAITLIST_NAME = 'caw-learner'

export const CAW_PROGRAM_DETAILS: ProgramDetails = {
  slug: 'college-admissions-workshops',
  title: 'College Admissions Workshops',
  descriptionTiny: 'Get advice from college students',
  descriptionShort: 'Navigate your college prep journey with confidence',
  description:
    'Navigate your college journey with confidence: from finding schools to submitting applications',
  worldSlug: COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
  fallbackWorldSlug: COLLEGE_PREP_WORLD_SLUG,
  programType: 'workshop',
  learnerWaitlistName: CAW_LEARNER_WAITLIST_NAME,
  tutorWaitlistName: 'caw-tutor',
  partnerships: ['Coalition', 'BigFuture'],
  imageUrl: '/programs/program-cards/caw.png',
  primaryColor: 'blue.solid',
  bgColor: 'blue.subtle',
}

export const CAW_TUTOR_FAQ_URL = 'https://coda.io/@shw/college-admissions-workshops-tutor-hub'

export const CAW_EMAIL_DATA: ProgramEmailData = {
  programManagerFirstName: 'Rebecca',
  tutorFaqUrl: CAW_TUTOR_FAQ_URL,
  learnerFaqUrl: getArticleUrl('what-are-college-admissions-workshops'),
}

export const COLLEGE_ADMISSIONS_WORKSHOPS_BENEFITS: Record<string, ProgramBenefit[]> = {
  tutor: CAW_PROGRAM_TUTOR_BENEFITS,
  fellowship: [
    {
      id: 'leadership-experience',
      text: 'Gain leadership, teaching, and startup experience.',
      faIcon: faLightbulb,
      iconProps: { color: 'blue500' },
    },
    {
      id: 'volunteer-hours',
      text: 'Accumulate volunteer hours to strengthen grad applications.',
      faIcon: faFileCertificate,
      iconProps: { color: 'green500' },
    },
    {
      id: 'recognition',
      text: 'Get recognition and have your name featured on the Schoolhouse website.',
      faIcon: faStar,
      iconProps: { color: 'yellow700' },
    },
    {
      id: 'career-coaching',
      text: 'Access career coaching workshops in the College Lounge subworld.',
      faIcon: faChalkboardTeacher,
      iconProps: { color: 'purple500' },
    },
  ],
}
